import { ReloadButton } from '@components/game/abilities/ReloadButton';
import { EnergyIndicator } from '@components/game/EnergyIndicator';
import { Ability } from '@components/game/abilities/Ability';
import { MessageEmitter } from '@phaser/events';
import { useTranslation } from 'react-i18next';
import { sendMessage } from '@api/websocket';
import { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

export function AbilityBar() {
  const [health, setHealth] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [needsTarget, setNeedsTarget] = useState(-1);
  const [cooldowns, setCooldowns] = useState<Shared.AbilitySync[]>([]);

  const { t } = useTranslation(undefined, { keyPrefix: 'Game.Abilities' });
  const { userData, id } = useAppStore();

  const chassis = userData.chassis.find(c => c.active)?.key;
  const data = Shared.chassisData.find(c => c.key === chassis);

  const abilities = data?.abilities
    .map(a => Shared.abilityData.find(ab => ab.key === a))
    .filter(a => !!a) ?? [];

  const useAbility = async (ability: Shared.AbilityData) => {
    if (ability.requiresTarget) {
      setNeedsTarget(ability.id);
      MessageEmitter.emit('needAbilityTarget', ability.id);
    } else {
      onAbilityTarget(ability.id);
      MessageEmitter.emit('clearAbilityTarget');
    }
  };

  const clearTarget = () => {
    setNeedsTarget(-1);
    MessageEmitter.emit('clearAbilityTarget');
  };

  const selfTarget = () => {
    onAbilityTarget(needsTarget, id);
  };

  const onAbilityTarget = (abilityId: number, targetId?: string) => {
    setNeedsTarget(-1);
    sendMessage(new Shared.PlayerAbilityUseMessage({
      abilityCode: abilityId,
      targetId,
    }));
    MessageEmitter.emit('clearAbilityTarget');
  };

  const onPlayerSync = (message: Shared.PlayerSyncMessage) => {
    if (!id.startsWith(message.id)) return;
    setCooldowns(message.abilities);
    setEnergy(message.energy);
    setHealth(message.health);
  };

  useEffect(() => {
    MessageEmitter.on('onAbilityTarget', onAbilityTarget);
    Shared.MessageRegistry.emitter.on(Shared.PlayerSyncMessage.Opcode, onPlayerSync);
    return () => {
      MessageEmitter.off('onAbilityTarget', onAbilityTarget);
      Shared.MessageRegistry.emitter.off(Shared.PlayerSyncMessage.Opcode, onPlayerSync);
    };
  });

  return (
    <div
      className="bg-tertiary border-t border-secondary h-16 ability-bar"
      style={{ boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0px 30px' }}
    >
      <EnergyIndicator />

      {needsTarget >= 0 && (
        <div className="p-2 px-4 flex items-center justify-between">
          <p className="text-xl active">{t('selectTarget')}</p>

          <button className="p-1 text-sm flex items-center justify-center btn-active px-2" onClick={selfTarget}>
            {t('self')}
          </button>

          <button className="w-10 h-10 flex items-center justify-center" onClick={clearTarget}>
            <IconX  />
          </button>
        </div>
      )}

      <div className={`${needsTarget < 0 ? 'flex' : 'hidden'} p-2 px-4 justify-between w-full`}>
        <ReloadButton />

        {abilities.map(ability => (
          <Ability
            cooldowns={cooldowns}
            onClick={useAbility}
            ability={ability}
            key={ability.id}
            energy={energy}
            health={health}
          />
        ))}
      </div>
    </div>
  );
}